import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, NgZone, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { SipService } from '../../services/sip.service';
import { VoipWorkstationService } from '../../services/voip-workstation.service';
import { AlarmStateService } from '../../services/alarm-state.service';
import { UserService } from '../../../shared/services/user.service';
import { SocketService } from '../../../shared/services/socket.service';
import { ModalService } from '../../../shared/services/modal.service';
import { CallInfo, CallState, CallDirection, RegistrationState, Workstation, WaitingCall, WorkstationSession, TransferTarget, TransferResult, ConsultCall } from '../../models/voip.models';
import { TranslateService } from '@ngx-translate/core';
import { audioWarnings } from './floating-phone-audio-warnings';
import { phoneKey } from './floating-phone-phone-key';

@Component({
    selector: 'app-floating-phone',
    templateUrl: './floating-phone.component.html',
    styleUrls: ['./floating-phone.component.scss']
})
export class FloatingPhoneComponent implements OnInit, OnDestroy, AfterViewInit {

    // Drag state
    isDragging: boolean = false;
    private dragStartX: number = 0;
    private dragStartY: number = 0;
    private widgetStartX: number = 0;
    private widgetStartY: number = 0;
    private hasMoved: boolean = false;
    widgetPosition: { right: number; bottom: number } = { right: 24, bottom: 24 };
    private static readonly STORAGE_KEY = 'floatingPhonePosition';

    // UI state
    isExpanded: boolean = false;
    isMinimized: boolean = true;

    // VoIP state
    voipAvailable: boolean = false;
    voipChecked: boolean = false; // Czy sprawdzono dostępność VoIP
    showWidget: boolean = false; // Czy pokazać widget (nawet gdy VoIP niedostępny)
    // Sprawdzenie dostępności VoIP padło (sieć / błąd serwera). To NIE to samo, co
    // odpowiedź available:false, w której backend jednoznacznie mówi, że VoIP nie jest
    // dla tego konta. Rozróżnienie jest potrzebne, bo ponawiać ma sens tylko to pierwsze.
    private voipCheckFailed: boolean = false;
    hasSession: boolean = false;
    // Ratownik z własnym kontem SIP (ParamedicSipAccount) — backend melduje mu sesję,
    // ale nie ma jej w sip_workstation, więc /logout i /break odpowiadają mu błędem 400.
    // Operacje na stanowisku po prostu go nie dotyczą i nie są mu pokazywane.
    hasDedicatedAccount: boolean = false;
    availableWorkstations: Workstation[] = [];
    currentWorkstationName: string = '';
    // Drobny opis stanowiska w widgecie: login SIP, numer managera, kolejki (informacyjnie)
    currentWorkstationInfo: string = '';
    mainPhoneNumber: string = '';
    // Czy stanowisko ma podpięty aparat biurkowy. Steruje wyłącznie widocznością
    // przełącznika — bez aparatu nie ma sensu proponować dzwonienia z telefonu.
    currentWorkstationHasDeskPhone: boolean = false;
    // Wybór ratownika: dzwonimy z aparatu czy ze słuchawek. Trzymany w localStorage,
    // bo to preferencja stanowiska pracy, a nie stan sesji — po odświeżeniu strony
    // ratownik ma zastać to, co ustawił.
    useDeskPhone: boolean = false;
    isDialingFromDesk: boolean = false;
    // Rozmowa zlecona na aparat. Widget nie ma nad nią kontroli — nie zna jej stanu,
    // nie policzy czasu i nie rozłączy jej — więc zamiast udawać panel rozmowy
    // pokazujemy pasek informacyjny, dopóki ratownik go nie zamknie.
    deskCallActive: boolean = false;
    deskCallNumber: string = '';
    // Przekazanie do Centrum Społecznego robi się na aparacie (TRAN) — widget tylko je zgłasza do ERP.
    deskTransferReported: boolean = false;
    // Czas rozmowy z aparatu liczony od wykrycia odebrania (webhook `callanswer` dochodzi z kilkusekundowym opóźnieniem).
    deskCallDuration: number = 0;
    private deskCallTimer: any = null;
    // Wychodząca z aparatu: od kiedy szukać jej zdarzeń w centrali (epoch s), dopóki nie znamy unique_id.
    private deskOutgoingSince = 0;
    deskHangupBusy: boolean = false;
    deskHangupError: string = '';
    deskTransferInfo: string = '';
    // unique_id połączenia z centrali — po nim sprawdzamy, czy rozmowa na aparacie się skończyła
    private deskCallUniqueId: string = '';
    private deskCallDialled = false;
    showWorkstationSelect: boolean = false;
    isInitializing: boolean = false;
    // Komunikat błędu logowania na stanowisko (najczęściej: stanowisko zajęte przez
    // innego opiekuna). Wcześniej błąd trafiał tylko do konsoli, więc kolejne kliknięcia
    // w to samo stanowisko wyglądały jakby nic się nie działo.
    workstationError: string = '';

    // Cykliczne odświeżanie listy stanowisk. Samo zdarzenie 'workstationChange' nie
    // wystarcza: gdy nie dotrze (zerwany socket, brak eventu z backendu), stanowisko
    // zajęte w międzyczasie przez innego opiekuna zostaje na liście jako wolne aż do
    // przeładowania strony — i kliknięcie w nie kończy się błędem (335).
    private static readonly WORKSTATION_POLL_MS = 10000;
    // Co 2 s jak na TC: rozmowa na aparacie trwa krótko, a modal ma się pojawić
    // jeszcze w jej trakcie, nie po fakcie.
    private static readonly DESK_CALL_POLL_MS = 1500;
    // Po tylu sekundach bez żadnego zdarzenia centrali pasek wychodzącej z aparatu znika.
    private static readonly DESK_OUTGOING_GIVE_UP_S = 120;
    // Ile po zakończeniu dzwonienia w przeglądarce pytamy o rozmowę odebraną na aparacie
    // (webhook `callanswer` dochodzi przez NDV2 zwykle w 1–3 s).
    private static readonly DESK_CALL_BURST_MS = 20000;
    private deskCallBurstUntil = 0;
    private deskCallTick = 0;
    private deskCallPollTimer: any = null;
    // Rozmowy odebrane W PRZEGLĄDARCE (ostatnie 9 cyfr + czas). Centrala wysyła `callanswer`
    // także dla nich, więc po rozłączeniu desk-call zwróciłby to samo połączenie i otworzył
    // drugi modal — takie zdarzenia pomijamy.
    private webphoneAnswered: { digits: string; at: number }[] = [];
    // = okno desk-call w backendzie (DESK_CALL_WINDOW_SECONDS = 120) + zapas na opóźnienie webhooka
    private static readonly WEBPHONE_ANSWER_MEMORY_MS = 150 * 1000;
    private workstationPollTimer: any = null;

    // SIP state
    registrationState: RegistrationState = 'unregistered';
    callInfo: CallInfo = {
        state: 'idle',
        direction: null,
        remoteNumber: null,
        startTime: null,
        duration: 0
    };

    phoneNumber: string = '';
    dialingName: string = ''; // Nazwa osoby do której dzwonimy (z click-to-call)
    isMuted: boolean = false;
    hasIncomingCall: boolean = false;

    // #372: połączenia, które przyszły gdy telefon był zajęty — widoczne na widgecie zamiast
    // przepadać. Po zakończeniu bieżącej rozmowy pierwsze z nich samo wraca na telefon.
    waitingCalls: WaitingCall[] = [];
    notLoggedInMessage: string = ''; // Komunikat gdy użytkownik nie jest zalogowany do SIP

    // Przekazanie rozmowy (SIP REFER, port z widgetu TC): agenci CRK ze stanem z centrali
    // + Centrum Społeczne Poznań, gdy rozmowa jest z seniorem z flagą poznan_hotline_transfer.
    keypadOpen: boolean = false;
    transferOpen: boolean = false;
    transferTargets: TransferTarget[] = [];
    transferTarget: TransferTarget | null = null;
    transferLoading: boolean = false;
    transferBusy: boolean = false;
    // Po przyjętym REFER centrala trzyma naszą nogę jeszcze do ~45 s, a kolejne REFER-y
    // dostają wtedy 603/481 — przyciski zostają zablokowane do końca rozmowy.
    transferDone: boolean = false;
    transferInfo: string = '';
    transferError: string = '';
    consultCall: ConsultCall | null = null;
    consultTarget: TransferTarget | null = null;
    private consultStartedAt: Date | null = null;
    private crkTargets: TransferTarget[] = [];
    // „Przekaż" na numer zewnętrzny: blind REFER z rozmowy kolejkowej SuperVOIP rozłącza dzwoniącego
    // (test TC 4.09), więc dzwonimy do celu i sami klikamy „Połącz", gdy odbierze.
    private consultAutoComplete = false;
    // Opcjonalna notatka dla Centrum Społecznego (Poznań) → ERP w callbacku przekazania.
    transferNote = '';
    // „Wróć" wysłał już consult_cancelled — koniec konsultacji z subskrypcji nie dubluje zdarzenia.
    private consultCancelled = false;
    // Numer Centrum Społecznego z find-patient-by-phone, przypięty do ostatnich 9 cyfr numeru
    // rozmówcy — odpowiedź przychodzi asynchronicznie (z modalem alarmu), po odebraniu.
    private poznanHotline: { digits: string; number: string } | null = null;

    // Brak urządzeń audio: wyjścia (głośniki — nie usłyszymy pacjenta) / wejścia (mikrofon — pacjent nas nie usłyszy).
    noAudioOutput: boolean = false;
    noAudioInput: boolean = false;
    // Zamknięty pasek wraca dopiero, gdy urządzenie się pojawi i znów zniknie — stanowiska
    // z aparatem w ogóle nie mają mikrofonu, więc stały czerwony pasek tylko przeszkadzał.
    audioWarningDismissed = {
        input: FloatingPhoneComponent.readDismissed('voipNoAudioInputDismissed'),
        output: FloatingPhoneComponent.readDismissed('voipNoAudioOutputDismissed')
    };
    private deviceChangeHandler: (() => void) | null = null;

    // Break state
    isOnBreak: boolean = false;
    breakStartedAt: Date | null = null;
    breakDuration: number = 0;
    private breakTimer: any = null;

    // 352: rozmowa zapisana już przy zamykaniu strony. Zdarzenie o jej zakończeniu może
    // jeszcze dojść (SIP kończy sesję asynchronicznie) — wtedy zwykła ścieżka zapisu
    // dopisałaby ten sam wpis drugi raz.
    private callLoggedOnUnload: boolean = false;

    // Alarm modal state
    private previousCallState: CallState = 'idle';
    private alarmModalShown: boolean = false;

    // Kolejka modali alarmowych VOIP. Bez niej modale z kolejnych połączeń nakładały się
    // na siebie: gdy operator nie odpowiedział na modal poprzedniego połączenia i odebrał
    // następne, otwierał się drugi modal na wierzchu pierwszego. Po kliknięciu "Tak" na
    // wszystkich każdy nadpisywał sessionStorage['activeAlarmData'] i nawigował na nowo,
    // więc zapisywał się / otwierał tylko ostatni alarm. Teraz pokazujemy je pojedynczo:
    // następny modal startuje dopiero po zamknięciu poprzedniego, więc każdy zostaje obsłużony.
    private alarmModalPending: boolean = false;
    private pendingAlarmQueue: { remoteNumber: string; direction: CallDirection }[] = [];

    // Ile czekamy na backendowy alarm dla nieznanego numeru, zanim otworzymy okno lokalnie.
    private static readonly UNKNOWN_ALARM_TIMEOUT_MS = 8000;

    private subscriptions: Subscription[] = [];
    private ringtoneAudio: HTMLAudioElement | null = null;

    private boundOnMouseMove: (e: MouseEvent) => void;
    private boundOnMouseUp: (e: MouseEvent) => void;
    private boundOnTouchMove: (e: TouchEvent) => void;
    private boundOnTouchEnd: (e: TouchEvent) => void;

    constructor(
        private sipService: SipService,
        private workstationService: VoipWorkstationService,
        private alarmStateService: AlarmStateService,
        private userService: UserService,
        private socketService: SocketService,
        private modalService: ModalService,
        private router: Router,
        private translateService: TranslateService,
        private ngZone: NgZone,
        private elementRef: ElementRef
    ) {
        this.boundOnMouseMove = this.onMouseMove.bind(this);
        this.boundOnMouseUp = this.onMouseUp.bind(this);
        this.boundOnTouchMove = this.onTouchMove.bind(this);
        this.boundOnTouchEnd = this.onTouchEnd.bind(this);
    }

    ngOnInit(): void {
        this.loadPosition();
        this.checkVoipAvailability();
        this.setupSubscriptions();

        // Sprawdź urządzenia audio (mikrofon + głośniki) i reaguj na podpięcie/odpięcie.
        this.checkAudioDevices();
        const md = navigator.mediaDevices as MediaDevices;
        if (md && md.addEventListener) {
            this.deviceChangeHandler = () => this.checkAudioDevices();
            md.addEventListener('devicechange', this.deviceChangeHandler);
        }
    }

    // Odświeżenie strony / zamknięcie karty w trakcie przerwy: zapisz jej koniec w bazie.
    // NIE wylogowujemy ze stanowiska — odświeżenie nie powinno kończyć zmiany operatora
    // (sesja zostaje, przerwa dostaje czas zakończenia). Bez tego przerwa "wisiała"
    // otwarta, licznik przerwy po przeładowaniu odtwarzał się ze starego czasu startu,
    // a backendowy licznik kolejnej przerwy liczył się błędnie.
    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        if (this.hasSession && this.isOnBreak) {
            this.workstationService.endBreakBeacon();
        }
        this.logCallOnUnload();
    }

    // 352: przeładowanie strony (po obsłudze alarmu robi je sos-request, poza tym zwykłe F5)
    // zabija rozmowę razem z kartą, a zdarzenie o jej zakończeniu przychodzi z centrali za
    // późno, żeby zwykły POST zdążył polecieć — rozmowa nie trafiała do historii w ogóle.
    // Zapisujemy tylko rozmowy odebrane, bo `startTime` pojawia się dopiero w chwili
    // odebrania: połączenie, które jeszcze dzwoniło, po przeładowaniu może wejść drugi raz
    // i zapisać się samo, a wpis stąd byłby wtedy drugim wierszem tej samej rozmowy.
    private logCallOnUnload(): void {
        const call = this.callInfo;
        if (!call.startTime || !call.remoteNumber || !call.direction) {
            return;
        }
        // Rozmowa ucięta zakończeniem obsługi alarmu dobiegła końca normalnie — to operator
        // skończył pracę. „Przerwane" zostaje dla F5 i zamknięcia karty, które ją urywają.
        const status = this.alarmStateService.wasAlarmFinished() ? 'completed' : 'interrupted';
        const answeredAt = call.startTime.toISOString();
        this.callLoggedOnUnload = true;
        this.workstationService.logCallBeacon({
            direction: call.direction,
            phoneNumber: call.remoteNumber,
            status: status,
            duration: call.duration,
            startedAt: answeredAt,
            answeredAt: answeredAt
        });
    }

    /**
     * Wykrywa brak urządzeń audio:
     *  - mikrofon (audioinput) — pacjent nas nie usłyszy; działa we wszystkich przeglądarkach,
     *  - głośniki (audiooutput) — my nie usłyszymy pacjenta; wykrywalne tylko tam, gdzie
     *    jest wybór wyjścia (Chromium/Edge – setSinkId). Firefox nie wystawia 'audiooutput',
     *    więc dla wyjścia nie ostrzegamy (fałszywe alarmy).
     */
    private async checkAudioDevices(): Promise<void> {
        try {
            const md = navigator.mediaDevices as MediaDevices;
            if (!md || !md.enumerateDevices) {
                this.noAudioInput = false;
                this.noAudioOutput = false;
                return;
            }
            const devices = await md.enumerateDevices();
            const supportsOutput = typeof (HTMLMediaElement.prototype as any).setSinkId === 'function';
            const warnings = audioWarnings(devices, supportsOutput);
            this.noAudioInput = warnings.noAudioInput;
            this.noAudioOutput = warnings.noAudioOutput;
            if (!this.noAudioInput && this.audioWarningDismissed.input) {
                this.setAudioWarningDismissed('input', false);
            }
            const enumerationTrusted = devices.some(d => !!d.label);
            if (!this.noAudioOutput && enumerationTrusted && this.audioWarningDismissed.output) {
                this.setAudioWarningDismissed('output', false);
            }
            // Paski ostrzeżeń podwyższają panel (np. po odpięciu mikrofonu w trakcie pracy).
            this.reclampIfOpen();
        } catch {
            this.noAudioInput = false;
            this.noAudioOutput = false;
        }
    }

    dismissAudioWarning(kind: 'input' | 'output'): void {
        this.setAudioWarningDismissed(kind, true);
        this.reclampIfOpen();
    }

    private setAudioWarningDismissed(kind: 'input' | 'output', value: boolean): void {
        this.audioWarningDismissed[kind] = value;
        const key = kind === 'input' ? 'voipNoAudioInputDismissed' : 'voipNoAudioOutputDismissed';
        try { localStorage.setItem(key, value ? '1' : '0'); } catch (e) {}
    }

    private static readDismissed(key: string): boolean {
        try { return localStorage.getItem(key) === '1'; } catch (e) { return false; }
    }

    ngAfterViewInit(): void {
        // Ensure position is applied after view init
    }

    private checkVoipAvailability(): void {
        // Poczekaj na załadowanie danych użytkownika
        this.subscriptions.push(
            this.userService.currentUser.pipe(
                filter(user => user && Object.keys(user).length > 0),
                take(1)
            ).subscribe(user => {
                const isManager = user.role === 'ROLE_API_MANAGER';
                const isParamedic = user.role === 'ROLE_API_CAREGIVER' && user.user_id && user.user_id.type === 'Paramedic';

                // console.log('FloatingPhone - User loaded:', {
                //     role: user.role,
                //     isManager,
                //     isParamedic
                // });

                // Widget VoIP widoczny tylko dla managera i opiekuna typu paramedic
                if (isManager || isParamedic) {
                    this.loadWorkstations();
                    this.subscribeToWorkstationChanges();
                    this.startWorkstationPolling();
                }
            })
        );
    }

    private loadWorkstations(): void {
        // console.log('FloatingPhone - Loading workstations...');

        this.workstationService.getAvailableWorkstations().subscribe(
            response => {
                // console.log('FloatingPhone - Workstations response:', response);
                this.voipChecked = true;

                if (this.voipCheckFailed) {
                    // Sprzątamy wyłącznie po własnym komunikacie o nieudanym sprawdzeniu.
                    // Bezwarunkowe czyszczenie skasowałoby powód odrzucenia rejestracji
                    // SIP (339), który trafia w to samo pole zupełnie inną drogą.
                    this.voipCheckFailed = false;
                    this.notLoggedInMessage = '';
                    this.showWidget = false;
                }

                if (response.available) {
                    this.voipAvailable = true;
                    this.sipService.markVoipAvailable();
                    this.mainPhoneNumber = response.mainPhoneNumber || '';
                    this.showWidget = true;
                    this.hasDedicatedAccount = response.hasDedicatedAccount === true;

                    if (response.hasSession) {
                        this.hasSession = true;
                        this.currentWorkstationName = response.currentWorkstation?.name || '';
                        this.currentWorkstationInfo = this.workstationInfo(response.currentWorkstation);
                        this.applyDeskPhoneAvailability(response.currentWorkstation?.usesDeskPhone === true);
                        console.log('FloatingPhone - Has session, initializing SIP');
                        this.initializeSip();
                    } else {
                        // Backend nie widzi sesji — zejdź z ekranu dzwonienia (po nieudanym
                        // wylogowaniu panel zostawał na nim z martwym telefonem).
                        this.forgetWorkstationSession();
                        this.availableWorkstations = response.workstations || [];
                        this.showWorkstationSelect = true;
                        console.log('FloatingPhone - Available workstations:', this.availableWorkstations);
                    }

                    // Zawartość panelu właśnie się zmieniła (lista stanowisk / ekran dzwonienia),
                    // a więc i jego wysokość — przytnij pozycję, żeby panel mieścił się w oknie.
                    this.reclampIfOpen();
                } else {
                    // console.log('FloatingPhone - VoIP not available:', response.message);
                }
            },
            error => {
                console.error('FloatingPhone - Error checking VoIP availability:', error);
                this.voipChecked = true;
                // Dotąd zostawało samo console.error: showWidget zostawał na false, więc
                // operator nie widział ani telefonu, ani powodu jego braku. Pokazujemy
                // widget z komunikatem, a shouldRefreshWorkstations() dopuszcza ponowienie
                // — inaczej jedynym wyjściem byłoby przeładowanie strony.
                this.voipCheckFailed = true;
                this.showWidget = true;
                this.notLoggedInMessage = this.translateService.instant('VOIP.AVAILABILITY_CHECK_FAILED');
            }
        );
    }

    // Autoodświeżanie listy stanowisk. Lista jest pobierana raz, przy starcie widgetu,
    // więc gdy w międzyczasie ktoś inny zajął stanowisko, u pozostałych operatorów
    // zostawało ono na liście jako wolne aż do przeładowania strony — klikali w nie
    // i dostawali błąd (335). Backend rozsyła zmiany sesji stanowisk zdarzeniem
    // 'workstationChange' (tego samego używa panel managera i sidebar VOIP).
    // Zdarzenie przychodzi z socket.io, czyli spoza strefy Angulara — bez ngZone.run
    // lista odświeżyłaby się w modelu, ale nie na ekranie.
    private subscribeToWorkstationChanges(): void {
        this.subscriptions.push(
            this.socketService.onWorkstationChange().subscribe(() => {
                // Operator z własną sesją nie ogląda listy stanowisk (widzi ekran dzwonienia),
                // więc cudze logowania nie mają czego u niego odświeżać.
                if (this.hasSession) {
                    return;
                }
                this.ngZone.run(() => this.loadWorkstations());
            })
        );
    }

    // Zapasowe odpytywanie na wypadek nieodebranego 'workstationChange'.
    // Interwał chodzi poza strefą Angulara — inaczej każde tyknięcie uruchamiałoby
    // detekcję zmian, także wtedy gdy nic nie robi.
    private startWorkstationPolling(): void {
        this.stopWorkstationPolling();
        this.ngZone.runOutsideAngular(() => {
            this.workstationPollTimer = setInterval(() => {
                if (!this.shouldRefreshWorkstations()) {
                    return;
                }
                this.ngZone.run(() => this.loadWorkstations());
            }, FloatingPhoneComponent.WORKSTATION_POLL_MS);
        });
    }

    /**
     * Czy pobranie listy stanowisk ma co zmienić na ekranie. Wspólne dla odpytywania
     * i dla ręcznego otwarcia panelu, żeby oba wchodziły w to samo: operator z własną
     * sesją widzi ekran dzwonienia, nie listę, a zwinięty widget nie ma czego odświeżać.
     *
     * Nieudane sprawdzenie dostępności też się liczy — wtedy ponowienie jest jedyną
     * drogą powrotu bez przeładowania strony. Przy jednoznacznej odpowiedzi
     * available:false nie ma czego ponawiać: VoIP po prostu nie jest dla tego konta.
     */
    private shouldRefreshWorkstations(): boolean {
        return this.isExpanded
            && !this.hasSession
            && (this.voipAvailable || this.voipCheckFailed);
    }

    private stopWorkstationPolling(): void {
        if (this.workstationPollTimer) {
            clearInterval(this.workstationPollTimer);
            this.workstationPollTimer = null;
        }
    }

    /**
     * Wykrywanie rozmowy PRZYCHODZĄCEJ odebranej na aparacie. Przeglądarka nie bierze
     * w niej udziału, więc sip.js o niej nie wie — jedynym źródłem jest webhook z centrali,
     * który backend wystawia przez `/caregiver/voip/desk-call`.
     *
     * Interwał chodzi poza strefą Angulara, jak odpytywanie stanowisk; do strefy wracamy
     * dopiero gdy jest co pokazać.
     */
    private startDeskCallPolling(): void {
        this.stopDeskCallPolling();
        this.ngZone.runOutsideAngular(() => {
            this.deskCallPollTimer = setInterval(() => {
                this.deskCallTick++;
                // Trwa rozmowa na aparacie — co ~3 s jej koniec (także przy karcie w tle: pasek, który
                // nie znika, myli ratownika), a wychodzącej z aparatu najpierw jej unique_id.
                if (this.deskCallActive && this.deskCallTick % 2 === 0) {
                    if (this.deskCallUniqueId) {
                        this.ngZone.run(() => this.checkDeskCallEnded());
                    } else if (this.deskOutgoingSince) {
                        this.ngZone.run(() => this.resolveOutgoingDeskCall());
                    }
                }
                // Zaraz po tym, jak w przeglądarce przestało dzwonić przychodzące (odebrał aparat
                // albo dzwoniący się rozłączył) — co tyknięcie; poza tym stale co ~3 s. Samo okno
                // po dzwonieniu w przeglądarce gubiło odbiory (28.10: karta w tle, webphone zajęty
                // innym połączeniem z kolejki) i ratownik zostawał bez okna alarmu.
                const burst = Date.now() < this.deskCallBurstUntil;
                if (!(burst || this.deskCallTick % 2 === 0) || !this.shouldPollDeskCall()) {
                    return;
                }
                this.ngZone.run(() => this.checkDeskCall());
            }, FloatingPhoneComponent.DESK_CALL_POLL_MS);
        });
    }

    /**
     * Odpytujemy tylko wtedy, gdy rozmowa faktycznie mogłaby iść przez aparat: karta jest
     * widoczna, stanowisko ma telefon, a webphone NIE prowadzi własnej rozmowy. Bez tego
     * ostatniego warunku rozmowa odebrana w przeglądarce (centrala i tak wyśle `callanswer`)
     * otworzyłaby drugi modal dla tego samego połączenia.
     */
    private shouldPollDeskCall(): boolean {
        // Bez warunku na widoczność karty: odbiór na aparacie przy karcie w tle też ma otworzyć
        // okno alarmu — po 120 s backend już go nie poda.
        // Bez warunku na brak paska: kolejna rozmowa odebrana na aparacie zastępuje poprzedni pasek.
        // Wcześniej pasek po rozmowie wychodzącej (bez unique_id, więc bez końca) wyłączał wykrywanie.
        return this.hasSession
            && this.currentWorkstationHasDeskPhone
            && this.callInfo.state === 'idle';
    }

    private checkDeskCall(): void {
        this.workstationService.getDeskCall().subscribe(
            response => {
                if (!response || !response.found || !response.number) {
                    return;
                }
                if (this.wasAnsweredOnWebphone(response.number)) {
                    // Ta rozmowa miała już modal z webphone'a (backend oznaczył zdarzenie jako zobaczone).
                    return;
                }
                // Centrala może zgłosić pierwszą nogę dial-desk jako przychodzącą z numerem docelowym.
                if (this.deskCallDialled && phoneKey(response.number) === phoneKey(this.deskCallNumber)) {
                    return;
                }
                if (this.deskCallActive) {
                    this.dismissDeskCall();
                }
                this.deskCallNumber = response.number;
                this.deskCallUniqueId = response.uniqueId || '';
                this.deskCallActive = true;
                this.startDeskCallTimer();
                // Ta sama ścieżka co przy rozmowie z webphone'a: dopasowanie pacjenta,
                // alarm, karta obsługi. Backend oznaczył już zdarzenie jako zobaczone.
                this.enqueueAlarmModal(response.number, 'incoming');
                // Numer Centrum Społecznego (senior z flagą) — dla podpowiedzi przekazania z aparatu.
                const number = response.number;
                this.workstationService.findPatientByPhone(number).subscribe(
                    res => this.rememberPoznanHotline(number, res),
                    () => {}
                );
            },
            () => {
                // Brak odpowiedzi nie może nic zepsuć — spróbujemy przy następnym tyknięciu.
            }
        );
    }

    // Widget nie ma połączenia z aparatem — o odłożeniu słuchawki wie tylko centrala (`callend`).
    private checkDeskCallEnded(): void {
        this.workstationService.getDeskCallStatus(this.deskCallUniqueId).subscribe(
            response => {
                if (response && response.ended) {
                    this.dismissDeskCall();
                }
            },
            () => {}
        );
    }

    private rememberWebphoneAnswer(number: string | null): void {
        const digits = phoneKey(number);
        if (!digits) {
            return;
        }
        const now = Date.now();
        this.webphoneAnswered = this.webphoneAnswered
            .filter(a => now - a.at < FloatingPhoneComponent.WEBPHONE_ANSWER_MEMORY_MS)
            .concat({ digits, at: now });
    }

    // Wpis jest zużywany: jedna rozmowa z webphone'a = jedno zdarzenie `callanswer`. Kolejna
    // rozmowa tego samego pacjenta odebrana już na aparacie musi dostać swój modal.
    private wasAnsweredOnWebphone(number: string): boolean {
        const digits = phoneKey(number);
        const now = Date.now();
        const idx = this.webphoneAnswered.findIndex(
            a => a.digits === digits && now - a.at < FloatingPhoneComponent.WEBPHONE_ANSWER_MEMORY_MS
        );
        if (!digits || idx === -1) {
            return false;
        }
        this.webphoneAnswered.splice(idx, 1);
        return true;
    }

    private stopDeskCallPolling(): void {
        if (this.deskCallPollTimer) {
            clearInterval(this.deskCallPollTimer);
            this.deskCallPollTimer = null;
        }
    }

    private setupSubscriptions(): void {
        this.subscriptions.push(
            this.sipService.getRegistrationState().subscribe(state => {
                this.registrationState = state;
                // Wyczyść komunikat gdy użytkownik się zarejestruje
                if (state === 'registered') {
                    this.notLoggedInMessage = '';
                }
            })
        );

        // #339: powód, dla którego telefon nie działa (najczęściej centrala odrzuciła
        // rejestrację źle skonfigurowanego konta SIP). Bez tego operator widział samo
        // „Rozłączono" i nie miał jak się domyślić, że to błąd konfiguracji.
        this.subscriptions.push(
            this.sipService.getError().subscribe(messageKey => {
                this.notLoggedInMessage = this.translateService.instant(messageKey);
            })
        );

        this.subscriptions.push(
            this.sipService.getCallInfo().subscribe(info => {
                const prevState = this.previousCallState;
                this.callInfo = info;
                this.previousCallState = info.state;

                // Przychodzące przestało dzwonić w przeglądarce bez odebrania tutaj — jeśli odebrał
                // aparat, desk-call zaraz to pokaże. Tylko wtedy pytamy backend (patrz pętla desk-call).
                if (prevState === 'ringing' && info.state === 'idle' && this.currentWorkstationHasDeskPhone) {
                    this.deskCallBurstUntil = Date.now() + FloatingPhoneComponent.DESK_CALL_BURST_MS;
                }

                // Auto-expand on incoming call
                if (info.state === 'ringing' && info.direction === 'incoming') {
                    this.hasIncomingCall = true;
                    this.isExpanded = true;
                    this.isMinimized = false;
                    this.reclampAfterRender();
                    this.playRingtone();
                } else {
                    this.hasIncomingCall = false;
                    this.stopRingtone();
                }

                // Wykryj przejście do stanu 'in-call' (połączenie odebrane/nawiązane).
                // alarmModalShown chroni przed podwójnym dodaniem w obrębie JEDNEGO połączenia
                // (in-call → on-hold → in-call). Kolejkowanie chroni przed nakładaniem modali
                // z RÓŻNYCH połączeń — patrz pendingAlarmQueue.
                if (info.state === 'in-call' && prevState !== 'in-call' && !this.alarmModalShown) {
                    this.alarmModalShown = true;
                    if ((info.direction || 'incoming') === 'incoming') {
                        this.rememberWebphoneAnswer(info.remoteNumber);
                    }
                    this.enqueueAlarmModal(info.remoteNumber, info.direction || 'incoming');
                }

                // Reset flagi po zakończeniu rozmowy
                if (info.state === 'idle') {
                    this.dialingName = '';
                    this.alarmModalShown = false;
                    this.resetTransfer();
                    this.keypadOpen = false;
                    this.clearRefusal('VOIP.END_CURRENT_CALL_FIRST');
                    // Po rozłączeniu wraca ekran wybierania numeru (wysoki — z klawiaturą),
                    // więc panel rośnie i przy telefonie ustawionym wysoko wyjeżdżał poza
                    // górną krawędź ekranu. Przycinamy pozycję do widoku.
                    if (prevState !== 'idle') {
                        this.reclampIfOpen();
                    }
                }
            })
        );

        this.subscriptions.push(
            this.sipService.getConsultCall().subscribe(call => {
                // Odbiorca się rozłączył / nie odebrał / „Wróć" — dzwoniący wrócił z holdu.
                if (!call && this.consultTarget && !this.transferDone) {
                    this.transferInfo = this.translateService.instant('VOIP.TRANSFER_CONSULT_ENDED', { target: this.consultTarget.name });
                    if (!this.consultCancelled && !this.transferBusy) {
                        this.reportTransfer('consult_ended', this.consultAutoComplete ? 'blind' : 'attended', this.consultTarget);
                    }
                }
                this.consultCall = call;
                if (!call) {
                    this.consultTarget = null;
                    this.consultAutoComplete = false;
                } else if (call.state === 'in-call' && this.consultAutoComplete && !this.transferBusy && !this.transferDone) {
                    this.consultAutoComplete = false;
                    this.transferComplete(true);
                }
            })
        );

        // Nasłuchuj na żądania wybrania numeru (click-to-call)
        this.subscriptions.push(
            this.sipService.getDialRequest().subscribe(request => {
                this.handleDialRequest(request.phoneNumber, request.name);
            })
        );

        // #372: kolejka oczekujących połączeń — pokaż ją operatorowi i rozwiń widget,
        // żeby nie dowiedział się o drugim dzwoniącym dopiero z historii połączeń.
        this.subscriptions.push(
            this.sipService.getWaitingCalls().subscribe(calls => {
                const appeared = calls.length > this.waitingCalls.length;
                this.waitingCalls = calls;
                if (appeared) {
                    this.isExpanded = true;
                    this.isMinimized = false;
                    this.reclampAfterRender();
                }
            })
        );

        // #372: oczekujący, który się rozłączył zanim doszła do niego kolej — do historii
        // jako nieodebrane (wcześniej nie było go nigdzie: ani na ekranie, ani w logach).
        this.subscriptions.push(
            this.sipService.getMissedCall().subscribe(missed => {
                this.workstationService.logCall({
                    direction: 'incoming',
                    phoneNumber: missed.remoteNumber || '',
                    status: 'missed',
                    endReason: missed.endReason,
                    duration: 0,
                    startedAt: missed.receivedAt ? missed.receivedAt.toISOString() : undefined,
                }).subscribe(
                    () => console.log('[VoIP] #372 nieodebrane z kolejki zalogowane:', missed.remoteNumber),
                    err => console.error('[VoIP] Error logging missed queued call:', err)
                );
            })
        );

        // Log call + auto-transkrypcja po zakończeniu rozmowy
        this.subscriptions.push(
            this.sipService.getCallEnded().subscribe(callData => {
                // 352: rozmowa poszła już beaconem przy zamykaniu strony — drugi zapis
                // zrobiłby z jednej rozmowy dwa wiersze w historii.
                if (this.callLoggedOnUnload) {
                    return;
                }

                // Log call to database
                const status = callData.duration > 0 ? 'completed' : 'missed';
                this.workstationService.logCall({
                    direction: callData.direction || 'outgoing',
                    phoneNumber: callData.remoteNumber || '',
                    status: status,
                    endReason: callData.endReason,
                    duration: callData.duration || 0,
                    startedAt: callData.startTime ? callData.startTime.toISOString() : undefined,
                    answeredAt: callData.duration > 0 && callData.startTime ? callData.startTime.toISOString() : undefined,
                }).subscribe(
                    () => console.log('[VoIP] Call logged successfully'),
                    err => console.error('[VoIP] Error logging call:', err)
                );

                if (callData.remoteNumber && callData.duration > 5) {
                    console.log('FloatingPhone - Call ended, scheduling auto-transcription:', callData);
                    // Czekaj 20 sekund aż SuperVOIP przetworzy nagranie
                    setTimeout(() => {
                        this.triggerAutoTranscription(callData.remoteNumber, callData.direction || 'unknown');
                    }, 20000);
                }
            })
        );
    }

    private triggerAutoTranscription(phoneNumber: string, direction: string): void {
        console.log('FloatingPhone - Triggering auto-transcription for:', phoneNumber);
        this.workstationService.autoTranscribe(phoneNumber, direction).subscribe(
            response => {
                if (response.success) {
                    console.log('FloatingPhone - Auto-transcription completed:', response.data?.ai_summary);
                }
            },
            error => {
                console.warn('FloatingPhone - Auto-transcription failed (recording may not be ready yet):', error);
                // Spróbuj jeszcze raz po 30 sekundach
                setTimeout(() => {
                    this.workstationService.autoTranscribe(phoneNumber, direction).subscribe(
                        response => {
                            if (response.success) {
                                console.log('FloatingPhone - Auto-transcription retry succeeded:', response.data?.ai_summary);
                            }
                        },
                        retryError => {
                            console.warn('FloatingPhone - Auto-transcription retry also failed:', retryError);
                        }
                    );
                }, 30000);
            }
        );
    }

    /**
     * Dodaj połączenie do kolejki modali alarmowych i (jeśli nic nie jest otwarte) pokaż od razu.
     * Numer/kierunek zapamiętujemy w chwili odebrania — kolejny modal może pojawić się później,
     * gdy this.callInfo dotyczy już innego połączenia.
     */
    private enqueueAlarmModal(remoteNumber: string | null, direction: CallDirection): void {
        if (!remoteNumber) {
            return;
        }
        this.pendingAlarmQueue.push({ remoteNumber, direction });
        this.processAlarmQueue();
    }

    /**
     * Pokaż następny modal z kolejki, o ile żaden nie jest aktualnie otwarty (jeden na raz).
     */
    private processAlarmQueue(): void {
        if (this.alarmModalPending) {
            return;
        }
        const next = this.pendingAlarmQueue.shift();
        if (!next) {
            return;
        }
        this.alarmModalPending = true;
        this.showAlarmModal(next.remoteNumber, next.direction);
    }

    /**
     * Modal alarmowy został zamknięty (Tak/Anuluj/błąd) — zwolnij blokadę i obsłuż następny w kolejce.
     */
    private finishAlarmModal(): void {
        this.alarmModalPending = false;
        this.processAlarmQueue();
    }

    /**
     * Pokaż modal alarmowy po odebraniu/nawiązaniu połączenia
     */
    private showAlarmModal(remoteNumber: string, callDirection: CallDirection): void {
        const activeAlarm = this.alarmStateService.getCurrentAlarm();
        console.log('FloatingPhone - Searching patient for phone:', remoteNumber, 'direction:', callDirection, 'activeAlarm:', activeAlarm);

        // Scenariusz A: Opiekun jest na stronie sos-request — powiąż połączenie z aktywnym alarmem
        if (activeAlarm) {
            this.workstationService.findPatientByPhone(remoteNumber).subscribe(
                response => {
                    this.rememberPoznanHotline(remoteNumber, response);
                    const patientName = response.found
                        ? `${response.patient.firstName} ${response.patient.lastName}`
                        : remoteNumber;

                    // Dzwoni INNY rozpoznany pacjent niż ten z otwartego alarmu — powiązanie nie ma
                    // sensu. Pytamy tylko, czy przejść do jego alarmu (bieżący formularz może
                    // mieć niezapisane zmiany, więc bez pytania nie wychodzimy z okna).
                    // Nieznany numer (np. rodzina) nadal może dotyczyć bieżącego alarmu.
                    const otherPatient = response.found && response.patient
                        && String(response.patient.imei || '') !== String(activeAlarm.imei || '');
                    // Odebrana rozmowa od innego seniora albo z nieznanego numeru = nowe zgłoszenie:
                    // od razu na wierzch, do poprzedniego alarmu ratownik wraca z listy alarmów.
                    if (callDirection === 'incoming' && (otherPatient || !response.found)) {
                        this.finishAlarmModal();
                        if (otherPatient) {
                            this.createCallAlarmAndNavigate(remoteNumber, callDirection, response.patient,
                                response.alarm ? response.alarm.id : undefined);
                        } else {
                            this.createCallAlarmUnknownPatient(remoteNumber, callDirection);
                        }
                        return;
                    }
                    if (otherPatient) {
                        this.modalService.openAccept(
                            this.translateService.instant('VOIP.CALL_OTHER_PATIENT_GO_TO_ALARM', { name: patientName }),
                            'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-CALL_OTHER_PATIENT_GO_TO_ALARM'
                        ).pipe(take(1)).subscribe(data => {
                            this.finishAlarmModal();
                            if (data.ok) {
                                this.createCallAlarmAndNavigate(remoteNumber, callDirection, response.patient,
                                    response.alarm ? response.alarm.id : undefined);
                            }
                        });
                        return;
                    }

                    const belongsToOpenAlarm = callDirection === 'outgoing' || response.found;
                    if (belongsToOpenAlarm) {
                        this.finishAlarmModal();
                        this.enrichExistingAlarm(remoteNumber, callDirection, activeAlarm.id, response.found ? response.patient : null);
                        return;
                    }

                    this.modalService.openAccept(
                        this.translateService.instant('VOIP.CALL_FROM_LINK_ALARM', { name: patientName }),
                        'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-CALL_FROM_LINK_ALARM'
                    ).pipe(take(1)).subscribe(data => {
                        this.finishAlarmModal();
                        if (data.ok) {
                            this.enrichExistingAlarm(remoteNumber, callDirection, activeAlarm.id, response.found ? response.patient : null);
                        }
                    });
                },
                error => {
                    this.finishAlarmModal();
                    console.error('FloatingPhone - Error finding patient:', error);
                }
            );
            return;
        }

        // Scenariusz B: Opiekun jest na głównym panelu
        this.workstationService.findPatientByPhone(remoteNumber).subscribe(
            response => {
                this.rememberPoznanHotline(remoteNumber, response);
                // Przychodzące: od razu do obsługi alarmu, bez okna „Pokazać alarm?" —
                // ratownik, który odebrał, i tak zawsze klikał „Tak" (zgłoszenie z telecentrum).
                if (callDirection === 'incoming') {
                    this.finishAlarmModal();
                    if (response.found) {
                        this.createCallAlarmAndNavigate(remoteNumber, callDirection, response.patient, response.alarm ? response.alarm.id : undefined);
                    } else {
                        this.createCallAlarmUnknownPatient(remoteNumber, callDirection);
                    }
                    return;
                }

                if (response.found) {
                    const patient = response.patient;
                    const existingAlarm = response.alarm;
                    const patientName = `${patient.firstName} ${patient.lastName}`;

                    if (existingAlarm) {
                        // Jest aktywny alarm — wzbogać go i nawiguj
                        this.modalService.openAccept(
                            this.translateService.instant('VOIP.CALL_PATIENT_ACTIVE_ALARM', { name: patientName, alarmType: this.getAlarmTypeLabel(existingAlarm.type) }),
                            'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-CALL_PATIENT_ACTIVE_ALARM'
                        ).pipe(take(1)).subscribe(data => {
                            this.finishAlarmModal();
                            if (data.ok) {
                                this.createCallAlarmAndNavigate(remoteNumber, callDirection, patient, existingAlarm.id);
                            }
                        });
                    } else {
                        // Brak aktywnego alarmu — utwórz nowy AlarmCall
                        this.modalService.openAccept(
                            this.translateService.instant('VOIP.CALL_PATIENT_SHOW_ALARM', { name: patientName }),
                            'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-CALL_PATIENT_SHOW_ALARM'
                        ).pipe(take(1)).subscribe(data => {
                            this.finishAlarmModal();
                            if (data.ok) {
                                this.createCallAlarmAndNavigate(remoteNumber, callDirection, patient);
                            }
                        });
                    }
                } else {
                    // Scenariusz C: Pacjent nie znaleziony — oferuj utworzenie alarmu z samym numerem
                    console.log('FloatingPhone - No patient found for phone:', remoteNumber);
                    // przychodzące obsłużone wyżej (bez okna) — tu zostają tylko wychodzące
                    const dirLabel = this.translateService.instant('VOIP.DIRECTION_OUTGOING');
                    this.modalService.openAccept(
                        this.translateService.instant('VOIP.CALL_UNKNOWN_PATIENT', { direction: dirLabel, number: remoteNumber }),
                        'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-CALL_UNKNOWN_PATIENT'
                    ).pipe(take(1)).subscribe(data => {
                        this.finishAlarmModal();
                        if (data.ok) {
                            this.createCallAlarmUnknownPatient(remoteNumber, callDirection);
                        }
                    });
                }
            },
            error => {
                this.finishAlarmModal();
                console.error('FloatingPhone - Error finding patient:', error);
            }
        );
    }

    /**
     * Wzbogać istniejący alarm o dane połączenia (Scenariusz A — z okna sos-request)
     */
    private enrichExistingAlarm(phone: string, direction: string, alarmId: number, patient: any): void {
        this.workstationService.createCallAlarm({
            phone: phone,
            direction: direction as 'incoming' | 'outgoing',
            patient_id: patient ? patient.id : null,
            imei: patient ? patient.imei : null,
            existing_alarm_id: alarmId
        }).subscribe(
            response => {
                console.log('FloatingPhone - Existing alarm enriched:', response.alarmId);
                // Nie nawigujemy — opiekun już jest na stronie sos-request
            },
            error => {
                console.error('FloatingPhone - Error enriching alarm:', error);
            }
        );
    }

    /**
     * Utwórz AlarmCall i nawiguj do okna obsługi alarmu (Scenariusz B — z głównego panelu)
     */
    private createCallAlarmAndNavigate(
        phone: string,
        direction: string,
        patient: any,
        existingAlarmId?: number
    ): void {
        this.workstationService.createCallAlarm({
            phone: phone,
            direction: direction as 'incoming' | 'outgoing',
            patient_id: patient.id,
            imei: patient.imei,
            existing_alarm_id: existingAlarmId || null
        }).subscribe(
            response => {
                if (response.success) {
                    const alarmId = response.alarmId;
                    console.log('FloatingPhone - Call alarm processed:', alarmId, 'isNew:', response.isNew);

                    // Zapisz dane alarmu do sessionStorage w formacie oczekiwanym przez sos-request
                    const alarmData = {
                        alarmID: alarmId,
                        imei: patient.imei,
                        user_id: patient.id,
                        first_name: patient.firstName,
                        last_name: patient.lastName,
                        discr: response.isNew ? 'alarmcall' : (response.discr || 'alarmcall'),
                        notifType: 'call',
                        phone_num: phone,
                        pulse: 0,
                        latitude: null,
                        longitude: null,
                        wear_error: 0,
                        alarmMeasurement_id: null,
                        received_data: Math.floor(Date.now() / 1000)
                    };
                    sessionStorage.setItem('activeAlarmData', JSON.stringify(alarmData));

                    this.openAlarmWindow(alarmId);
                }
            },
            error => {
                console.error('FloatingPhone - Error creating call alarm:', error);
                if (existingAlarmId) {
                    this.openAlarmWindow(existingAlarmId);
                } else if (patient.imei) {
                    this.router.navigateByUrl('/users/user/' + patient.imei);
                }
            }
        );
    }

    /**
     * Utwórz AlarmCall dla niezidentyfikowanego pacjenta (Scenariusz C).
     *
     * Okno obsługi MUSI się otworzyć niezależnie od tego, co zwróci backend — dla nieznanego
     * numeru '/create-call-alarm' potrafi odpowiedzieć bez alarmu, błędem albo (przy problemie
     * z siecią) nie odpowiedzieć wcale. Wcześniej w tym ostatnim przypadku kliknięcie "Tak"
     * nie robiło nic. Dlatego: jeden strażnik na nawigację (żeby okno nie otworzyło się dwa
     * razy) + limit czasu oczekiwania na backend.
     */
    private createCallAlarmUnknownPatient(phone: string, direction: string): void {
        let windowOpened = false;
        const openWindow = (alarmId: any, local: boolean) => {
            if (windowOpened) {
                return;
            }
            windowOpened = true;
            this.openUnknownAlarmWindow(phone, alarmId, local);
        };

        this.workstationService.createCallAlarm({
            phone: phone,
            direction: direction as 'incoming' | 'outgoing',
            patient_id: null,
            imei: null,
            existing_alarm_id: null
        }).pipe(
            take(1),
            timeout(FloatingPhoneComponent.UNKNOWN_ALARM_TIMEOUT_MS)
        ).subscribe(
            response => {
                const alarmId = response ? (response.alarmId || response.alarm_id) : null;
                if (response && response.success && alarmId) {
                    console.log('FloatingPhone - Unknown patient alarm created:', alarmId);
                    openWindow(alarmId, false);
                } else {
                    // Backend nie utworzył alarmu dla nieznanego numeru — i tak otwieramy
                    // okno obsługi lokalnie, żeby operator zobaczył zgłoszenie
                    // (nieznany użytkownik + numer dzwoniącego). Wcześniej okno w ogóle
                    // się nie otwierało.
                    console.warn('FloatingPhone - Backend did not create unknown alarm, opening local view');
                    openWindow('call-' + Date.now(), true);
                }
            },
            error => {
                console.error('FloatingPhone - Error creating unknown patient alarm:', error);
                openWindow('call-' + Date.now(), true);
            }
        );
    }

    /**
     * Otwiera okno obsługi alarmu dla nieznanego numeru (nieznany użytkownik + numer).
     * `local=true` gdy backend nie utworzył rekordu — sos-request renderuje z sessionStorage.
     */
    private openUnknownAlarmWindow(phone: string, alarmId: any, local: boolean): void {
        const alarmData: any = {
            alarmID: alarmId,
            imei: null,
            first_name: '',
            last_name: '',
            discr: 'alarmcall',
            notifType: 'call',
            phone_num: phone,
            pulse: 0,
            latitude: null,
            longitude: null,
            wear_error: 0,
            alarmMeasurement_id: null,
            received_data: Math.floor(Date.now() / 1000),
            localUnknown: local
        };
        sessionStorage.setItem('activeAlarmData', JSON.stringify(alarmData));
        this.openAlarmWindow(alarmId);
    }

    /**
     * Z okna alarmu do innego alarmu: ta sama trasa nie odtworzyłaby komponentu i w oknie zostałby
     * formularz poprzedniego zgłoszenia. Przystanek 'alarm-switch' (bez zmiany adresu) zamyka stare okno —
     * jego niezapisany opis trafia do szkicu i wraca, gdy ratownik otworzy tamten alarm z listy.
     */
    private openAlarmWindow(alarmId: any): void {
        const url = '/users/sos-request/' + alarmId;
        if (this.router.url.startsWith('/users/sos-request/') && this.router.url !== url) {
            this.router.navigateByUrl('/alarm-switch', { skipLocationChange: true }).then(() => this.router.navigateByUrl(url));
            return;
        }
        this.router.navigateByUrl(url);
    }

    /**
     * Pobierz etykietę typu alarmu
     */
    private getAlarmTypeLabel(type: string): string {
        switch (type) {
            case 'sos': return 'SOS';
            case 'fall': return this.translateService.instant('VOIP.ALARM_FALL');
            case 'zone': return this.translateService.instant('VOIP.ALARM_ZONE');
            case 'immo': return this.translateService.instant('VOIP.ALARM_IMMO');
            default: return this.translateService.instant('VOIP.ALARM_DEFAULT');
        }
    }

    /**
     * Obsłuż żądanie wybrania numeru z zewnętrznego komponentu
     */
    private handleDialRequest(phoneNumber: string, name?: string): void {
        console.log('FloatingPhone - Dial request received:', phoneNumber, name);

        // Wyczyść poprzedni komunikat
        this.notLoggedInMessage = '';

        // Pokaż widget (nawet jeśli VoIP niedostępny)
        this.showWidget = true;
        this.isExpanded = true;
        this.isMinimized = false;
        this.reclampAfterRender();

        // Ustaw numer i nazwę
        this.phoneNumber = phoneNumber;
        this.dialingName = name || '';

        // Sprawdź czy VoIP jest dostępny
        if (!this.voipAvailable) {
            if (this.voipChecked) {
                this.notLoggedInMessage = this.translateService.instant('VOIP.VOIP_NOT_AVAILABLE_ACCOUNT');
            } else {
                this.notLoggedInMessage = this.translateService.instant('VOIP.CHECKING_VOIP');
            }
            return;
        }

        // Sprawdź czy użytkownik jest zalogowany na stanowisku
        if (!this.hasSession) {
            this.notLoggedInMessage = this.translateService.instant('VOIP.LOGIN_TO_MAKE_CALLS');
            return;
        }

        // Sprawdź czy SIP jest zarejestrowany — z aparatu dzwoni centrala, przeglądarka nie jest potrzebna
        if (this.registrationState !== 'registered' && !this.currentWorkstationHasDeskPhone) {
            if (this.registrationState === 'registering' || this.isInitializing) {
                this.notLoggedInMessage = this.translateService.instant('VOIP.CONNECTING_PBX');
            } else {
                this.notLoggedInMessage = this.translateService.instant('VOIP.NO_PBX_CONNECTION');
            }
            return;
        }

        // Sprawdź czy nie jesteśmy na przerwie
        if (this.isOnBreak) {
            this.notLoggedInMessage = this.translateService.instant('VOIP.END_BREAK_TO_CALL');
            return;
        }

        // Sprawdź czy nie ma aktywnego połączenia
        if (this.callInfo.state !== 'idle') {
            this.notLoggedInMessage = this.translateService.instant('VOIP.END_CURRENT_CALL_FIRST');
            return;
        }

        // Wszystko OK - dzwoń
        setTimeout(() => {
            if (this.phoneNumber === phoneNumber) {
                this.makeCall();
            }
        }, 500);
    }

    private clearRefusal(messageKey: string): void {
        if (this.notLoggedInMessage === this.translateService.instant(messageKey)) {
            this.notLoggedInMessage = '';
        }
    }

    toggleExpand(): void {
        if (this.wasDragged()) return;
        this.isExpanded = !this.isExpanded;
        this.isMinimized = !this.isExpanded;
        if (this.isExpanded) {
            // Zapasowe odświeżenie na wypadek nieodebranego 'workstationChange'
            // (zerwany socket): otwierając panel operator widzi aktualne stany stanowisk.
            // Ten sam warunek co przy odpytywaniu — inaczej po nieudanym sprawdzeniu
            // dostępności otwarcie panelu nic by nie dawało i trzeba by czekać na cykl.
            if (this.shouldRefreshWorkstations()) {
                this.loadWorkstations();
            }
            this.reclampAfterRender();
        }
    }

    minimize(): void {
        this.isExpanded = false;
        this.isMinimized = true;
    }

    // Workstation methods
    loginToWorkstation(workstationId: number): void {
        this.isInitializing = true;
        this.workstationError = '';

        this.workstationService.loginToWorkstation(workstationId).subscribe(
            response => {
                if (response.success) {
                    this.hasSession = true;
                    this.currentWorkstationName = response.workstationName;
                    const ws = this.availableWorkstations.find(w => w.id === workstationId);
                    this.currentWorkstationInfo = this.workstationInfo(ws);
                    // Bez tego tryb aparatu (i nasłuch desk-call) włączał się dopiero po
                    // odświeżeniu strony — rozmowa odebrana na aparacie nie otwierała alarmu.
                    // Źródłem jest odpowiedź logowania; lista wolnych stanowisk bywa nieaktualna
                    // (ponowne logowanie na to samo stanowisko: było zajęte, więc go w niej nie ma).
                    this.applyDeskPhoneAvailability(
                        response.usesDeskPhone !== undefined ? response.usesDeskPhone === true : ws?.usesDeskPhone === true
                    );
                    this.showWorkstationSelect = false;
                    this.clearRefusal('VOIP.LOGIN_TO_MAKE_CALLS');
                    this.initializeSip();
                    // Krótka lista stanowisk zamienia się na wysoki ekran dzwonienia
                    // (klawiatura numeryczna) — bez przycięcia pozycji panel rozrastał się
                    // w górę poza ekran i nagłówek z przyciskami stawał się niedostępny.
                    this.reclampIfOpen();
                } else {
                    // Odpowiedź 200 z success=false (np. stanowisko zajęte). Wcześniej ta
                    // gałąź nie robiła NIC — isInitializing zostawało na true, więc wszystkie
                    // przyciski stanowisk stawały się na stałe nieaktywne i kolejne kliknięcia
                    // były bez efektu.
                    this.handleWorkstationLoginFailure();
                }
            },
            error => {
                console.error('Error logging into workstation:', error);
                this.handleWorkstationLoginFailure();
            }
        );
    }

    // Wspólna obsługa nieudanego logowania na stanowisko: pokaż komunikat operatorowi
    // i odśwież listę, żeby zajęte stanowisko od razu było oznaczone jako zajęte.
    private handleWorkstationLoginFailure(): void {
        this.isInitializing = false;
        this.workstationError = this.translateService.instant('VOIP.WORKSTATION_LOGIN_FAILED');
        this.loadWorkstations();
    }

    /**
     * Aparat stacjonarny dzwoni niezależnie od panelu, ale okno alarmu otwiera się tylko
     * przy sesji na stanowisku (desk-call szuka ratownika po `sip_workstation`). Po wylogowaniu
     * z aplikacji sesja znika, a aparat dzwoni dalej — rozmowy idą bez alarmu w panelu.
     */
    get deskPhoneWithoutSession(): boolean {
        return !this.hasSession && this.availableWorkstations.some(ws => ws.usesDeskPhone === true);
    }

    /** „SIP 1291207 · +48221228410 · KolejkaX" — drobny opis stanowiska, tylko informacyjnie. */
    workstationInfo(ws: Workstation | null | undefined): string {
        if (!ws) {
            return '';
        }
        const parts: string[] = [];
        if (ws.sipLogin) {
            parts.push('SIP ' + ws.sipLogin);
        }
        if (this.mainPhoneNumber) {
            parts.push(this.mainPhoneNumber);
        }
        if (ws.queues && ws.queues.length) {
            parts.push(ws.queues.join(', '));
        }
        return parts.join(' · ');
    }

    // Stanowisko zajęte przez innego opiekuna. Backend nie zawsze wystawia `isOccupied`
    // na liście dostępnych stanowisk — gdy go brak, traktujemy stanowisko jak wolne
    // (zachowanie jak dotychczas), a powód niepowodzenia pokazujemy po próbie logowania.
    isWorkstationOccupied(ws: Workstation): boolean {
        return ws.isOccupied === true;
    }

    getWorkstationOccupiedText(ws: Workstation): string {
        return ws.currentCaregiverName
            ? this.translateService.instant('VOIP.WORKSTATION_OCCUPIED_BY', { name: ws.currentCaregiverName })
            : this.translateService.instant('VOIP.OCCUPIED');
    }

    /**
     * Rejestracja w centrali to co innego niż sesja stanowiska (426): opiekun z własnym
     * kontem SIP nie ma stanowiska, więc bez tego przycisku nie ma czym postawić telefonu
     * z powrotem. Operatorowi na stanowisku wystarcza „Wyloguj", dopóki telefon działa.
     */
    get showPhoneToggle(): boolean {
        if (!this.hasSession || this.callInfo.state !== 'idle' || this.registrationState === 'registering') {
            return false;
        }
        return this.hasDedicatedAccount || !this.isPhoneConnected;
    }

    get isPhoneConnected(): boolean {
        return this.registrationState === 'registered';
    }

    togglePhone(): void {
        if (this.isPhoneConnected) {
            this.sipService.disconnect();
        } else {
            this.initializeSip();
        }
    }

    logoutFromWorkstation(): void {
        // SIP rozłączamy PRZED pytaniem backendu celowo: konto SIP należy do stanowiska,
        // nie do opiekuna, więc gdy tylko backend je zwolni, może się na nie zalogować
        // ktoś inny — dwie rejestracje tego samego konta to zdublowany kontakt w centrali.
        // Ceną tej kolejności jest to, że gałąź błędu musi umieć przywrócić rejestrację.
        this.sipService.disconnect().then(() => {
            // Jeśli operator jest na przerwie, najpierw zapisz jej koniec (defensywnie —
            // nie zakładamy, że backendowy /logout sam zamyka otwartą przerwę).
            if (this.isOnBreak) {
                this.workstationService.endBreak().subscribe(
                    () => this.doWorkstationLogout(),
                    error => {
                        // Wylogowanie idzie dalej mimo to, ale przerwa może zostać otwarta
                        // po stronie backendu — bez śladu w konsoli nie byłoby jak tego dojść.
                        console.error('Error ending break before logout:', error);
                        this.doWorkstationLogout();
                    }
                );
            } else {
                this.doWorkstationLogout();
            }
        });
    }

    private doWorkstationLogout(): void {
        this.workstationService.logoutFromWorkstation().subscribe(
            () => {
                this.forgetWorkstationSession();
                this.registrationState = 'unregistered';
                this.workstationError = '';
                this.notLoggedInMessage = '';
                this.loadWorkstations();
            },
            error => {
                console.error('Error logging out:', error);
                // Wylogowanie nie doszło do skutku (ratownik z własnym kontem SIP nie ma
                // sesji stanowiska, więc backend odpowiada mu błędem), a SIP jest już
                // rozłączony. Bez odświeżenia stanu panel zostawał z hasSession === true
                // i martwym telefonem aż do przeładowania strony: wszystkie ścieżki
                // autoodświeżania są bramkowane przez !hasSession.
                this.notLoggedInMessage = this.translateService.instant('VOIP.LOGOUT_FAILED');
                this.loadWorkstations();
            }
        );
    }

    /**
     * Zapomina o sesji stanowiska — panel wraca do wyboru stanowiska. Przerwa jest częścią
     * tej sesji, a initializeSip ustawia isOnBreak wyłącznie na true (nigdy na false):
     * niedoczyszczona flaga przeżyłaby ponowne zalogowanie i pokazała pasek przerwy
     * z tykającym licznikiem dla przerwy, której nie ma.
     */
    private forgetWorkstationSession(): void {
        this.hasSession = false;
        this.currentWorkstationName = '';
        this.currentWorkstationInfo = '';
        this.applyDeskPhoneAvailability(false);
        this.dismissDeskCall();
        this.isOnBreak = false;
        this.breakStartedAt = null;
        this.stopBreakTimer();
    }

    /**
     * Ustawia dostępność aparatu dla bieżącego stanowiska i przywraca zapamiętany
     * wybór ratownika. Gdy stanowisko aparatu nie ma, tryb zawsze wraca na słuchawki —
     * inaczej po przesiadce na stanowisko bez telefonu kliknięcie „zadzwoń” poszłoby
     * w endpoint, który i tak odpowie błędem.
     */
    private applyDeskPhoneAvailability(hasDeskPhone: boolean): void {
        this.currentWorkstationHasDeskPhone = hasDeskPhone;

        if (!hasDeskPhone) {
            this.useDeskPhone = false;
            this.stopDeskCallPolling();
            return;
        }

        // Nasłuch rozmów odebranych na aparacie nie zależy od przełącznika: ratownik
        // może dzwonić ze słuchawek, a mimo to odebrać przychodzącą na telefonie.
        this.startDeskCallPolling();

        // Bez zapamiętanego wyboru domyślnie aparat — stanowiska z telefonem zwykle nie mają
        // mikrofonu przy komputerze, a tryb słuchawek kończył się modalem „brak mikrofonu”.
        try {
            this.useDeskPhone = localStorage.getItem('voipUseDeskPhone') !== '0';
        } catch (e) {
            this.useDeskPhone = true;
        }
    }

    /** Przełącza tryb „dzwonię z aparatu / ze słuchawek”. */
    toggleDeskPhoneMode(): void {
        this.useDeskPhone = !this.useDeskPhone;
        try {
            localStorage.setItem('voipUseDeskPhone', this.useDeskPhone ? '1' : '0');
        } catch (e) {
            // brak localStorage nie może blokować dzwonienia
        }
    }

    /**
     * Chowa pasek rozmowy na aparacie. Świadomie NIE rozłącza — widget nie ma żadnej
     * kontroli nad połączeniem, które centrala zestawiła poza przeglądarką. Rozmowę
     * kończy się odłożeniem słuchawki.
     */
    dismissDeskCall(): void {
        this.deskCallActive = false;
        this.deskCallNumber = '';
        this.deskCallUniqueId = '';
        this.deskCallDialled = false;
        this.deskTransferReported = false;
        this.transferNote = '';
        this.deskHangupError = '';
        this.deskTransferInfo = '';
        this.deskOutgoingSince = 0;
        this.stopDeskCallTimer();
    }

    /** Wychodząca z aparatu: unique_id z webhooka centrali — od tej chwili jest licznik końca i „Rozłącz”. */
    private resolveOutgoingDeskCall(): void {
        const since = this.deskOutgoingSince;
        this.workstationService.getDeskCallOutgoing(since).subscribe(
            response => {
                if (since !== this.deskOutgoingSince) {
                    return;
                }
                if (response && response.found) {
                    this.deskCallUniqueId = response.uniqueId || '';
                    if (response.ended) {
                        this.dismissDeskCall();
                    }
                } else if (Date.now() / 1000 - since > FloatingPhoneComponent.DESK_OUTGOING_GIVE_UP_S) {
                    // Centrala nie zestawiła rozmowy (brak jakiegokolwiek zdarzenia) — pasek nie może wisieć.
                    this.dismissDeskCall();
                }
            },
            () => {}
        );
    }

    private startDeskCallTimer(): void {
        this.stopDeskCallTimer();
        const startedAt = Date.now();
        this.deskCallTimer = setInterval(() => {
            this.deskCallDuration = Math.floor((Date.now() - startedAt) / 1000);
        }, 1000);
    }

    private stopDeskCallTimer(): void {
        if (this.deskCallTimer) {
            clearInterval(this.deskCallTimer);
            this.deskCallTimer = null;
        }
        this.deskCallDuration = 0;
    }

    /**
     * Rozmowy z aparatu nie da się przekazać przez centralę — backend ją rozłącza, a centrala dzwoni
     * do Centrum Społecznego i po odebraniu łączy je z seniorem. Senior usłyszy ponowny dzwonek.
     */
    transferDeskCall(): void {
        const number = this.deskCallPoznanHotline;
        if (!number || !this.deskCallUniqueId || this.deskHangupBusy) {
            return;
        }
        this.modalService.openAccept(
            this.translateService.instant('VOIP.DESK_TRANSFER_CALLBACK_CONFIRM', { number }),
            'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-DESK_TRANSFER_CALLBACK_CONFIRM'
        ).pipe(take(1)).subscribe(data => {
            if (!data.ok) {
                return;
            }
            this.deskHangupBusy = true;
            this.deskHangupError = '';
            this.workstationService.transferDeskCall(this.deskCallUniqueId).subscribe(
                res => {
                    this.deskHangupBusy = false;
                    if (res && res.success) {
                        this.reportDeskTransfer();
                        this.deskTransferInfo = this.translateService.instant('VOIP.DESK_TRANSFER_CALLBACK_DONE', { number });
                    } else {
                        this.deskHangupError = (res && res.error) || this.translateService.instant('VOIP.DESK_TRANSFER_CALLBACK_FAILED');
                    }
                },
                err => {
                    this.deskHangupBusy = false;
                    this.deskHangupError = (err && err.error) || this.translateService.instant('VOIP.DESK_TRANSFER_CALLBACK_FAILED');
                }
            );
        });
    }

    get canHangupDeskCall(): boolean {
        return !!this.deskCallUniqueId;
    }

    /** Rozmowę z aparatu rozłącza centrala — widget w niej nie uczestniczy. */
    hangupDeskCall(): void {
        if (!this.deskCallUniqueId || this.deskHangupBusy) {
            return;
        }
        this.deskHangupBusy = true;
        this.deskHangupError = '';
        this.workstationService.hangupDeskCall(this.deskCallUniqueId).subscribe(
            res => {
                this.deskHangupBusy = false;
                if (res && res.ended) {
                    this.dismissDeskCall();
                } else {
                    this.deskHangupError = (res && res.error) || this.translateService.instant('VOIP.DESK_CALL_HANGUP_FAILED');
                }
            },
            err => {
                this.deskHangupBusy = false;
                this.deskHangupError = (err && err.error) || this.translateService.instant('VOIP.DESK_CALL_HANGUP_FAILED');
            }
        );
    }

    get deskCallPoznanHotline(): string | null {
        const digits = phoneKey(this.deskCallNumber);
        return this.poznanHotline && digits && this.poznanHotline.digits === digits ? this.poznanHotline.number : null;
    }

    /** Rozmowy z aparatu widget nie przekaże — ratownik robi to klawiszem TRAN, a tu tylko zgłasza do ERP. */
    reportDeskTransfer(): void {
        const number = this.deskCallPoznanHotline;
        if (!number || this.deskTransferReported) {
            return;
        }
        this.deskTransferReported = true;
        this.workstationService.reportTransferEvent({
            event: 'completed',
            type: 'blind',
            target_number: number,
            target_name: this.translateService.instant('VOIP.TRANSFER_POZNAN'),
            caller_number: this.deskCallNumber,
            direction: 'incoming',
            note: (this.transferNote || '').trim() || null
        });
    }

    // Break methods
    toggleBreak(): void {
        if (this.isOnBreak) {
            this.endBreak();
        } else {
            this.startBreak();
        }
    }

    startBreak(): void {
        this.workstationService.startBreak().subscribe(
            response => {
                if (response.success) {
                    this.isOnBreak = true;
                    this.breakStartedAt = new Date();
                    this.breakDuration = 0;
                    this.startBreakTimer();
                    // Pasek przerwy podwyższa panel — przytnij pozycję do widoku.
                    this.reclampIfOpen();
                }
            },
            error => {
                console.error('Error starting break:', error);
            }
        );
    }

    endBreak(): void {
        this.workstationService.endBreak().subscribe(
            response => {
                if (response.success) {
                    this.isOnBreak = false;
                    this.breakStartedAt = null;
                    this.stopBreakTimer();
                    this.clearRefusal('VOIP.END_BREAK_TO_CALL');
                }
            },
            error => {
                console.error('Error ending break:', error);
            }
        );
    }

    private startBreakTimer(): void {
        this.stopBreakTimer();
        this.breakTimer = setInterval(() => {
            if (this.breakStartedAt) {
                // clamp do 0 — przy drobnym przesunięciu zegara nie pokazujemy ujemnego czasu
                this.breakDuration = Math.max(0, Math.floor((new Date().getTime() - this.breakStartedAt.getTime()) / 1000));
            }
        }, 1000);
    }

    /**
     * Przerwa jest stanem sesji stanowiska, więc odpowiedź backendu rozstrzyga o niej
     * w całości. Wcześniej ustawiane było wyłącznie true — przerwa zakończona gdzie
     * indziej (druga karta, panel managera) zostawałaby na ekranie razem z licznikiem.
     */
    private applyBreakState(session: WorkstationSession): void {
        this.isOnBreak = session.isOnBreak === true;
        this.breakStartedAt = this.isOnBreak && session.breakStartedAt ? new Date(session.breakStartedAt) : null;
        if (this.breakStartedAt) {
            this.startBreakTimer();
        } else {
            this.stopBreakTimer();
        }
    }

    private stopBreakTimer(): void {
        if (this.breakTimer) {
            clearInterval(this.breakTimer);
            this.breakTimer = null;
        }
        this.breakDuration = 0;
    }

    private async initializeSip(): Promise<void> {
        this.isInitializing = true;

        // Pobierz szczegóły sesji (w tym status przerwy)
        this.workstationService.getMySession().subscribe(
            session => this.applyBreakState(session),
            error => console.error('Error getting session:', error)
        );

        const success = await this.sipService.initialize();
        this.isInitializing = false;

        if (!success) {
            console.error('Failed to initialize SIP');
        }
    }

    // Call methods
    async makeCall(): Promise<void> {
        if (!this.phoneNumber) {
            return;
        }

        // Tryb aparatu omija WebRTC w całości: dzwoni centrala, nie przeglądarka.
        // Dlatego celowo NIE sprawdzamy tu ani rejestracji SIP.js, ani mikrofonu —
        // oba warunki blokowałyby połączenie, które wcale przez przeglądarkę nie idzie.
        if (this.useDeskPhone && this.currentWorkstationHasDeskPhone) {
            this.dialFromDeskPhone();
            return;
        }

        // Tryb słuchawek na stanowisku z aparatem, a przeglądarka nie widzi mikrofonu albo nie jest
        // zalogowana do centrali — dzwonimy z aparatu i wracamy na ten tryb, zamiast blokować połączenie.
        if (this.currentWorkstationHasDeskPhone) {
            await this.checkAudioDevices();
            if (this.noAudioInput || this.registrationState !== 'registered') {
                this.toggleDeskPhoneMode();
                this.dialFromDeskPhone();
                return;
            }
        }

        if (this.registrationState !== 'registered') {
            return;
        }
        // Pre-flight: bez mikrofonu / bez zgody połączenie i tak by padło
        // ("NotFoundError: Requested device not found") — pokaż czytelny modal zamiast błędu w konsoli.
        if (!(await this.ensureAudioInput())) {
            return;
        }
        this.sipService.makeCall(this.phoneNumber);
    }

    /**
     * Zleca centrali połączenie z aparatu biurkowego. Centrala dzwoni najpierw na konto
     * SIP stanowiska, ratownik podnosi słuchawkę, a dopiero potem łączy się numer.
     */
    private dialFromDeskPhone(): void {
        if (this.isDialingFromDesk) {
            return;
        }

        this.isDialingFromDesk = true;

        const number = this.phoneNumber;

        this.workstationService.dialFromDeskPhone(number).subscribe(
            () => {
                this.isDialingFromDesk = false;

                // Widget NIE dowie się, że rozmowa trwa: cała ścieżka alarmu wisi na stanie
                // 'in-call' z sip.js, a przy aparacie przeglądarka w rozmowie nie uczestniczy.
                // Bez tego wywołania rozmowa z aparatu nie zostawiłaby ŻADNEGO śladu —
                // ani alarmu, ani wpisu w historii. Puszczamy dokładnie tę samą ścieżkę,
                // co połączenie wychodzące z webphone'a: dopasowanie pacjenta, alarm, karta.
                this.dismissDeskCall();
                this.deskCallNumber = number;
                this.deskCallDialled = true;
                this.deskCallActive = true;
                this.deskOutgoingSince = Math.floor(Date.now() / 1000);
                this.startDeskCallTimer();
                this.phoneNumber = '';
                this.enqueueAlarmModal(number, 'outgoing');
            },
            (error) => {
                this.isDialingFromDesk = false;
                // Backend zwraca gotowy, ludzki komunikat (zajęte konto, brak rejestracji
                // aparatu, debounce) — pokazujemy go wprost, bo niesie konkretną poradę.
                const message = error?.error || this.translateService.instant('VOIP.DESK_PHONE_DIAL_ERROR');
                this.modalService.open(message, undefined, true, 'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.DIAL-FROM-DESK-PHONE');
            }
        );
    }

    /** Sprawdza dostęp do mikrofonu; przy braku urządzenia/zgody pokazuje modal i zwraca false. */
    private async ensureAudioInput(): Promise<boolean> {
        const md = navigator.mediaDevices as MediaDevices;
        if (!md || !md.getUserMedia) {
            this.modalService.open(this.translateService.instant('VOIP.NO_AUDIO_DEVICE'), undefined, true, 'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.VOIP-NO_AUDIO_DEVICE');
            return false;
        }
        try {
            const stream = await md.getUserMedia({ audio: true });
            stream.getTracks().forEach(t => t.stop()); // od razu zwolnij mikrofon
            // Po przyznaniu uprawnień etykiety urządzeń stają się widoczne —
            // przelicz ostrzeżenia o mikrofonie/głośnikach na wiarygodnych danych.
            this.checkAudioDevices();
            return true;
        } catch (e) {
            const name = (e && (e as any).name) || '';
            const msg = (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError')
                ? this.translateService.instant('VOIP.MIC_PERMISSION_DENIED')
                : this.translateService.instant('VOIP.NO_AUDIO_DEVICE');
            this.modalService.open(msg, undefined, true, 'EVENTS.VOIP.COMPONENTS.FLOATING-PHONE.POPUP.ENSURE-AUDIO-INPUT');
            return false;
        }
    }

    answerCall(): void {
        this.stopRingtone();
        this.sipService.answerCall();
    }

    rejectCall(): void {
        this.stopRingtone();
        this.sipService.rejectCall();
    }

    /** #372: odrzuć konkretne połączenie z kolejki oczekujących. */
    rejectWaitingCall(call: WaitingCall): void {
        this.sipService.rejectWaitingCall(call.id);
    }

    hangup(): void {
        this.sipService.hangup();
    }

    toggleHold(): void {
        this.sipService.toggleHold();
    }

    toggleMute(): void {
        this.isMuted = !this.isMuted;
        this.sipService.setMute(this.isMuted);
    }

    toggleKeypad(): void {
        this.keypadOpen = !this.keypadOpen;
        this.reclampAfterRender();
    }

    // === Przekazanie rozmowy (CRK, Centrum Społeczne Poznań) ===

    toggleTransfer(): void {
        this.transferOpen = !this.transferOpen;
        this.transferError = '';
        if (this.transferOpen) {
            this.loadTransferTargets();
        }
        this.reclampAfterRender();
    }

    /**
     * Centrum Społeczne (tylko senior z flagą) + CRK ze stanem agentów z centrali. Bez stanu
     * ratownik przekazywałby w pustkę: test TC 4.09 — REFER na niezalogowanego agenta = CHANUNAVAIL.
     */
    private loadTransferTargets(): void {
        this.crkTargets = [];
        this.transferTarget = null;
        this.rebuildTransferTargets();
        this.transferLoading = true;
        this.workstationService.getTransferTargets().subscribe(
            res => {
                this.transferLoading = false;
                const queue: TransferTarget[] = res && res.queueNumber
                    ? [{ name: this.translateService.instant('VOIP.TRANSFER_CRK_QUEUE'), number: res.queueNumber, queue: true }]
                    : [];
                this.crkTargets = [...queue, ...((res && res.agents) || [])];
                this.rebuildTransferTargets();
            },
            () => {
                this.transferLoading = false;
            }
        );
        // Numer Poznania przychodzi z dopasowania w kolejce okien alarmu — gdy okno z poprzedniej
        // rozmowy jeszcze wisi albo odpowiedź nie zdążyła, celu brakowało. Dopytujemy sami.
        const remote = this.callInfo.remoteNumber;
        if (remote && !this.currentPoznanHotline()) {
            this.workstationService.findPatientByPhone(remote).subscribe(
                res => {
                    this.rememberPoznanHotline(remote, res);
                    this.rebuildTransferTargets();
                },
                () => {}
            );
        }
    }

    /** Centrum Społeczne (gdy senior ma flagę) + CRK; wybrany cel zostaje, póki jest na liście. */
    private rebuildTransferTargets(): void {
        const hotline = this.currentPoznanHotline();
        const poznan: TransferTarget[] = hotline
            ? [{ name: this.translateService.instant('VOIP.TRANSFER_POZNAN'), number: hotline, external: true }]
            : [];
        const selected = this.transferTarget ? this.transferTarget.number : null;
        this.transferTargets = [...poznan, ...this.crkTargets];
        this.transferTarget = this.transferTargets.find(t => t.number === selected) || this.transferTargets[0] || null;
        this.reclampIfOpen();
    }

    selectTransferTarget(target: TransferTarget): void {
        if (this.consultCall || !this.targetAvailable(target)) {
            return;
        }
        this.transferTarget = target;
        this.transferError = '';
    }

    targetAvailable(target: TransferTarget): boolean {
        return target.queue || target.external || target.loggedIn !== false;
    }

    targetStatusKey(target: TransferTarget): string {
        if (target.queue || target.external) {
            return '';
        }
        if (!target.loggedIn) {
            return 'VOIP.TRANSFER_AGENT_OFFLINE';
        }
        return target.busy ? 'VOIP.TRANSFER_AGENT_BUSY' : 'VOIP.TRANSFER_AGENT_AVAILABLE';
    }

    /** Przekazanie w ciemno. Kolejka CRK = pierwszy zalogowany wolny agent (REFER tylko na numery wewnętrzne). */
    async transferBlind(): Promise<void> {
        const target = this.transferTarget;
        if (!target || this.transferBusy || this.callInfo.state !== 'in-call') {
            return;
        }
        if (target.external) {
            return this.transferConsult(true);
        }
        const agents = this.transferTargets.filter(t => !t.queue && !t.external && t.loggedIn);
        const dest = target.queue ? (agents.find(t => !t.busy) || agents[0]) : target;
        if (!dest) {
            this.transferError = this.translateService.instant('VOIP.TRANSFER_NO_AGENT');
            return;
        }
        this.transferBusy = true;
        this.transferError = '';
        this.transferInfo = this.translateService.instant('VOIP.TRANSFER_IN_PROGRESS', { target: dest.name });
        const result = await this.sipService.transferBlind(dest.number);
        this.transferBusy = false;
        this.reportTransfer(result.ok ? 'completed' : 'failed', 'blind', dest, result,
            dest !== target ? target.number : null);
        this.showTransferResult(result, dest.name);
    }

    /**
     * Z konsultacją: dzwoniący czeka na holdzie, ratownik rozmawia z odbiorcą, potem „Połącz".
     * Kolejka dzwoni pod pełny numer CRK — „Połącz" łączy z konkretną rozmową, kto by nie odebrał.
     */
    /** `auto` = „Przekaż" na numer zewnętrzny: po odebraniu „Połącz" klika się samo (bez rozmowy z odbiorcą). */
    async transferConsult(auto: boolean = false): Promise<void> {
        const target = this.transferTarget;
        if (!target || this.transferBusy || this.consultCall || this.callInfo.state !== 'in-call') {
            return;
        }
        this.transferBusy = true;
        this.transferError = '';
        this.transferInfo = auto ? this.translateService.instant('VOIP.TRANSFER_IN_PROGRESS', { target: target.name }) : '';
        // Przed startem: odbiorca może odrzucić, zanim invite() się rozwiąże.
        this.consultTarget = target;
        this.consultStartedAt = new Date();
        this.consultCancelled = false;
        this.consultAutoComplete = auto;
        const started = await this.sipService.startConsultTransfer(target.number);
        this.transferBusy = false;
        this.reportTransfer(started ? 'consult_started' : 'consult_failed', auto ? 'blind' : 'attended', target);
        if (!started) {
            this.consultTarget = null;
            this.consultAutoComplete = false;
            this.transferInfo = '';
            this.transferError = this.translateService.instant('VOIP.TRANSFER_CONSULT_FAILED', { target: target.name });
        } else if (this.consultAutoComplete && this.consultCall && this.consultCall.state === 'in-call') {
            // Odebrał, zanim invite() się rozwiązało — subskrypcja pominęła to przez transferBusy.
            this.consultAutoComplete = false;
            this.transferComplete(true);
        }
    }

    async transferComplete(auto: boolean = false): Promise<void> {
        const target = this.consultTarget;
        if (!target || this.transferBusy || this.transferDone) {
            return;
        }
        this.transferBusy = true;
        this.transferError = '';
        this.transferInfo = this.translateService.instant('VOIP.TRANSFER_IN_PROGRESS', { target: target.name });
        const result = await this.sipService.completeConsultTransfer();
        this.transferBusy = false;
        this.reportTransfer(result.ok ? 'completed' : 'failed', auto ? 'blind' : 'attended', target, result);
        this.showTransferResult(result, target.name);
    }

    /** Rozłącz odbiorcę i wróć do dzwoniącego (zdjęcie z holdu robi sipService). */
    transferBack(): void {
        if (this.transferBusy || this.transferDone) {
            return;
        }
        if (this.consultTarget) {
            this.consultCancelled = true;
            this.reportTransfer('consult_cancelled', this.consultAutoComplete ? 'blind' : 'attended', this.consultTarget);
        }
        this.sipService.cancelConsultTransfer();
    }

    /** Callback do ERP (przez backend): kto, kogo i dokąd przekazał — PKOS pokazuje to Centrum Społecznemu. */
    private reportTransfer(event: string, type: string, target: TransferTarget, result?: TransferResult, requestedNumber?: string | null): void {
        this.workstationService.reportTransferEvent({
            event: event,
            type: type,
            target_number: target.number,
            target_name: target.name,
            requested_number: requestedNumber || null,
            code: result ? result.code : null,
            reason: result && !result.ok ? (result.reason || null) : null,
            caller_number: this.callInfo.remoteNumber,
            direction: this.callInfo.direction,
            call_started_at: this.callInfo.startTime ? new Date(this.callInfo.startTime).toISOString() : null,
            consult_started_at: (type === 'attended' || target.external) && this.consultStartedAt ? this.consultStartedAt.toISOString() : null,
            note: target.external ? ((this.transferNote || '').trim() || null) : null
        });
    }

    private showTransferResult(result: TransferResult, targetName: string): void {
        if (result.ok) {
            this.transferDone = true;
            this.transferInfo = this.translateService.instant('VOIP.TRANSFER_DONE', { target: targetName });
            return;
        }
        this.transferInfo = '';
        this.transferError = this.translateService.instant('VOIP.TRANSFER_FAILED', {
            target: targetName,
            reason: this.translateService.instant(result.reason || 'VOIP.TRANSFER_ERR_FAILED', { code: result.code })
        });
    }

    private resetTransfer(): void {
        this.transferOpen = false;
        this.transferBusy = false;
        this.transferDone = false;
        this.transferInfo = '';
        this.transferError = '';
        this.poznanHotline = null;
        this.transferNote = '';
    }

    /** find-patient-by-phone zwraca numer tylko dla seniora z flagą, gdy dzwoni sam senior (opaska/telefon). */
    private rememberPoznanHotline(remoteNumber: string, response: any): void {
        const number = response && response.found && response.patient ? response.patient.poznanHotlineNumber : null;
        if (number) {
            this.poznanHotline = { digits: phoneKey(remoteNumber), number: number };
        }
    }

    private currentPoznanHotline(): string | null {
        const digits = phoneKey(this.callInfo.remoteNumber);
        return this.poznanHotline && digits && this.poznanHotline.digits === digits ? this.poznanHotline.number : null;
    }

    onDigitPressed(digit: string): void {
        if (this.callInfo.state === 'in-call') {
            // Przytrzymane „0” daje „+”, a to nie jest ton.
            this.sipService.sendDtmf(digit === '+' ? '0' : digit);
        } else {
            this.phoneNumber += digit;
        }
    }

    backspace(): void {
        this.phoneNumber = this.phoneNumber.slice(0, -1);
    }

    // Helper methods
    getStatusText(): string {
        switch (this.registrationState) {
            case 'registered': return this.translateService.instant('VOIP.STATUS_CONNECTED');
            case 'registering': return this.translateService.instant('VOIP.STATUS_CONNECTING');
            case 'error': return this.translateService.instant('VOIP.STATUS_ERROR');
            default: return this.translateService.instant('VOIP.STATUS_DISCONNECTED');
        }
    }

    getCallStateText(): string {
        switch (this.callInfo.state) {
            case 'ringing': return this.callInfo.direction === 'incoming' ? this.translateService.instant('VOIP.CALL_INCOMING') : this.translateService.instant('VOIP.CALL_DIALING');
            case 'connecting': return this.translateService.instant('VOIP.STATUS_CONNECTING');
            case 'in-call': return this.translateService.instant('VOIP.CALL_IN_PROGRESS');
            case 'on-hold': return this.translateService.instant('VOIP.CALL_ON_HOLD');
            default: return '';
        }
    }

    formatDuration(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    private playRingtone(): void {
        try {
            if (!this.ringtoneAudio) {
                this.ringtoneAudio = new Audio('assets/sounds/ringtone.mp3');
                this.ringtoneAudio.loop = true;
            }
            this.ringtoneAudio.play().catch(e => console.log('Ringtone blocked by browser'));
        } catch (e) {
            console.error('Error playing ringtone:', e);
        }
    }

    private stopRingtone(): void {
        if (this.ringtoneAudio) {
            this.ringtoneAudio.pause();
            this.ringtoneAudio.currentTime = 0;
        }
    }

    // Drag methods
    onDragStart(event: MouseEvent | TouchEvent): void {
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        this.dragStartX = clientX;
        this.dragStartY = clientY;
        this.widgetStartX = this.widgetPosition.right;
        this.widgetStartY = this.widgetPosition.bottom;
        this.hasMoved = false;

        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', this.boundOnMouseMove);
            document.addEventListener('mouseup', this.boundOnMouseUp);
            document.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
            document.addEventListener('touchend', this.boundOnTouchEnd);
        });
    }

    private onMouseMove(event: MouseEvent): void {
        this.handleDragMove(event.clientX, event.clientY);
    }

    private onTouchMove(event: TouchEvent): void {
        event.preventDefault();
        this.handleDragMove(event.touches[0].clientX, event.touches[0].clientY);
    }

    private handleDragMove(clientX: number, clientY: number): void {
        const deltaX = this.dragStartX - clientX;
        const deltaY = this.dragStartY - clientY;

        if (!this.isDragging && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
            this.isDragging = true;
            this.hasMoved = true;
        }

        if (this.isDragging) {
            const next = this.clampPosition({
                right: this.widgetStartX + deltaX,
                bottom: this.widgetStartY + deltaY
            });

            this.ngZone.run(() => {
                this.widgetPosition = next;
            });
        }
    }

    // Przycina pozycję widgetu do bieżącego widoku (right/bottom liczone od krawędzi).
    // Rozmiar bierzemy z realnie wyrenderowanego elementu — rozwinięty panel (320px szer.,
    // zmienna wysokość) jest znacznie większy niż sama ikonka i przy pozycji blisko
    // krawędzi wychodził poza ekran (górą/lewą stroną), stając się nieklikalny.
    private clampPosition(pos: { right: number; bottom: number }): { right: number; bottom: number } {
        let w = 70;
        let h = 70;
        const host = this.elementRef && this.elementRef.nativeElement as HTMLElement;
        const widgetEl = host ? host.querySelector('.floating-phone-widget') as HTMLElement : null;
        if (widgetEl) {
            const rect = widgetEl.getBoundingClientRect();
            if (rect.width > 0) w = Math.min(Math.ceil(rect.width) + 8, window.innerWidth);
            if (rect.height > 0) h = Math.min(Math.ceil(rect.height) + 8, window.innerHeight);
        }
        const maxRight = Math.max(0, window.innerWidth - w);
        const maxBottom = Math.max(0, window.innerHeight - h);
        return {
            right: Math.max(0, Math.min(maxRight, pos.right)),
            bottom: Math.max(0, Math.min(maxBottom, pos.bottom))
        };
    }

    // Przycięcie pozycji ma sens tylko dla widocznego, rozwiniętego panelu — zminimalizowana
    // ikonka jest mała i już mieści się w widoku (a niepotrzebny zapis pozycji przesuwałby ją).
    private reclampIfOpen(): void {
        if (this.showWidget && !this.isMinimized) {
            this.reclampAfterRender();
        }
    }

    // Po otwarciu/rozwinięciu panelu DOM zmienia rozmiar dopiero po renderze —
    // przycinamy pozycję w następnym ticku, żeby panel w całości mieścił się na ekranie.
    private reclampAfterRender(): void {
        setTimeout(() => {
            this.ngZone.run(() => {
                this.widgetPosition = this.clampPosition(this.widgetPosition);
                this.savePosition();
            });
        }, 0);
    }

    // Zmiana rozmiaru okna LUB zoomu przeglądarki zmienia innerWidth/innerHeight —
    // bez tego zapisana pozycja (right/bottom) mogła wypaść poza ekran i telefon znikał
    // aż do odświeżenia. Po każdej zmianie przycinamy go z powrotem do widoku.
    @HostListener('window:resize')
    onWindowResize(): void {
        this.widgetPosition = this.clampPosition(this.widgetPosition);
        this.savePosition();
    }

    // Awaryjne wyśrodkowanie telefonu (gdyby mimo wszystko wyjechał poza ekran): Ctrl/Cmd + Alt + P.
    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.altKey && (event.key === 'p' || event.key === 'P')) {
            event.preventDefault();
            this.recenterWidget();
        }
    }

    recenterWidget(): void {
        const widget = 70;
        this.widgetPosition = this.clampPosition({
            right: Math.round((window.innerWidth - widget) / 2),
            bottom: Math.round((window.innerHeight - widget) / 2)
        });
        this.savePosition();
    }

    private onMouseUp(event: MouseEvent): void {
        this.finishDrag();
    }

    private onTouchEnd(event: TouchEvent): void {
        this.finishDrag();
    }

    private finishDrag(): void {
        document.removeEventListener('mousemove', this.boundOnMouseMove);
        document.removeEventListener('mouseup', this.boundOnMouseUp);
        document.removeEventListener('touchmove', this.boundOnTouchMove);
        document.removeEventListener('touchend', this.boundOnTouchEnd);

        if (this.isDragging) {
            this.savePosition();
        }

        // Reset after a small delay so click events don't fire
        setTimeout(() => {
            this.isDragging = false;
        }, 50);
    }

    /** Returns true if the drag moved — use to suppress click */
    wasDragged(): boolean {
        return this.hasMoved;
    }

    private savePosition(): void {
        try {
            localStorage.setItem(FloatingPhoneComponent.STORAGE_KEY, JSON.stringify(this.widgetPosition));
        } catch (e) {
            // localStorage may be unavailable
        }
    }

    private loadPosition(): void {
        try {
            const saved = localStorage.getItem(FloatingPhoneComponent.STORAGE_KEY);
            if (saved) {
                const pos = JSON.parse(saved);
                if (typeof pos.right === 'number' && typeof pos.bottom === 'number') {
                    // Clamp to current viewport
                    this.widgetPosition = this.clampPosition(pos);
                }
            }
        } catch (e) {
            // Use default position
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.stopWorkstationPolling();
        this.stopDeskCallPolling();
        this.stopBreakTimer();
        this.stopDeskCallTimer();
        this.stopRingtone();
        document.removeEventListener('mousemove', this.boundOnMouseMove);
        document.removeEventListener('mouseup', this.boundOnMouseUp);
        document.removeEventListener('touchmove', this.boundOnTouchMove);
        document.removeEventListener('touchend', this.boundOnTouchEnd);
        const md = navigator.mediaDevices as MediaDevices;
        if (md && md.removeEventListener && this.deviceChangeHandler) {
            md.removeEventListener('devicechange', this.deviceChangeHandler);
        }
    }
}
